import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { trim, get } from 'lodash'
import { Form, Formik, Field } from 'formik'
import * as Yup from 'yup'
import classNames from 'classnames'

import Layout from 'containers/Layout'
import LoadingIcon from 'components/atoms/LoadingIcon'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import apiPut from 'lib/network/apiPut'
import apiGet from 'lib/network/apiGet'
import notify from 'components/atoms/Notify'
import { setUser } from 'state/redux/actions/user.actions'
import useModal from 'hooks/useModal'
import ChangePassword from 'containers/ChangePassword'
import { UserIcon, MailIcon, LockClosedIcon, ChevronRightIcon, CameraIcon, PencilIcon, RefreshIcon, InformationCircleIcon } from '@heroicons/react/outline'

const profileSchema = Yup.object({
  fullName: Yup.string()
    .transform(value => trim(value == null ? '' : String(value)))
    .min(1)
    .max(200)
    .required(),
})

/** Reference: Material-style enterprise profile (surface / primary #091c2d) */
const cardShell = 'rounded-xl border border-[#c6c5d4]/10 bg-white shadow-[0_32px_64px_-12px_rgba(9,28,45,0.06)]'

const MyProfile = () => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const store = useStore()
  const user = useSelector(state => get(state, 'user.user'))
  const isRTL = i18n.language === 'ar'
  const fileInputRef = useRef(null)
  const [avatarBusy, setAvatarBusy] = useState(false)

  const [ChangePasswordModal, openChangePasswordModal] = useModal({
    content: <ChangePassword />,
    title: t('sidebar.changePassword'),
    dimensions: {
      height: 'auto',
      width: '500px',
      minHeight: 'auto',
      minWidth: '500px',
    },
  })

  const mergeSessionUser = async () => {
    const res = await apiGet('user', {}, false)
    if (res?.success && res.data) {
      const prev = get(store.getState(), 'user.user') || {}
      dispatch(setUser({ ...prev, ...res.data }))
    }
  }

  const profileImageUrl = user?.userProfile?.profile

  const onPickAvatar = async e => {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarBusy(true)
    const fd = new FormData()
    fd.append('avatar', file)
    const res = await apiPut('user/me/profile-picture', fd)
    setAvatarBusy(false)
    e.target.value = ''
    if (res?.success) {
      notify.success(t('accountProfile.avatarUpdated'))
      await mergeSessionUser()
    }
  }

  const openFilePicker = () => fileInputRef.current?.click()

  return (
    <Layout
      title={t('accountProfile.title')}
      breadcrumbs={[
        { name: t('sidebar.dashboard'), pathname: '/dashboard' },
        { name: t('accountProfile.title'), pathname: '/profile' },
      ]}
      background="#f7f9fb"
    >
      <ChangePasswordModal />
      <div className={classNames('min-h-full px-4 py-8 sm:px-8', isRTL && 'text-right')}>
        <div className="mx-auto max-w-2xl space-y-10">
          {/* Reference-style breadcrumb + hero */}
          <header className={classNames('space-y-2', isRTL ? 'text-right' : 'text-left')}>
            <nav className={classNames('flex flex-wrap items-center gap-2 text-sm font-medium text-[#454652]', isRTL && 'flex-row-reverse justify-end')} aria-label="Breadcrumb">
              <span>{t('accountProfile.breadcrumbParent')}</span>
              <ChevronRightIcon className={classNames('h-3.5 w-3.5 shrink-0 text-[#767683]', isRTL && 'rotate-180')} aria-hidden />
              <span className="font-semibold text-[#091c2d]">{t('accountProfile.title')}</span>
            </nav>
            <h1 className="text-3xl font-extrabold tracking-tight text-[#091c2d]">{t('accountProfile.pageHeading')}</h1>
            <p className="max-w-md text-sm leading-relaxed text-[#454652]">{t('accountProfile.subtitle')}</p>
          </header>

          {/* Profile photo — horizontal card, hover camera overlay */}
          <section className={classNames(cardShell, 'p-8', isRTL && 'text-right')}>
            <div className={classNames('flex flex-col items-stretch gap-8 sm:flex-row sm:items-center', isRTL && 'sm:flex-row-reverse')}>
              <div className="relative shrink-0 self-center sm:self-auto">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPickAvatar} />
                <button
                  type="button"
                  disabled={avatarBusy}
                  onClick={openFilePicker}
                  className="group relative block h-24 w-24 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#091c2d]/25 focus-visible:ring-offset-2 disabled:opacity-60"
                  aria-label={t('accountProfile.changePhoto')}
                >
                  <span className="absolute inset-0 rounded-full ring-4 ring-[#f2f4f6]" aria-hidden />
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="" className="relative z-0 h-full w-full rounded-full object-cover" />
                  ) : (
                    <div className="relative z-0 flex h-full w-full items-center justify-center rounded-full bg-[#eceef0]" aria-hidden>
                      <UserIcon className="h-11 w-11 text-[#767683]" strokeWidth={1.25} />
                    </div>
                  )}
                  <span className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-[#091c2d]/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    <CameraIcon className="h-8 w-8 text-white" strokeWidth={1.5} aria-hidden />
                  </span>
                  {avatarBusy ? (
                    <span className="absolute inset-0 z-20 flex items-center justify-center rounded-full bg-[#091c2d]/50">
                      <LoadingIcon className="text-white" />
                    </span>
                  ) : null}
                </button>
              </div>
              <div className="min-w-0 flex-1 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-[#091c2d]">{t('accountProfile.avatar')}</h2>
                  <p className="text-xs font-medium text-[#454652]">{t('accountProfile.avatarFormats')}</p>
                </div>
                <button
                  type="button"
                  disabled={avatarBusy}
                  onClick={openFilePicker}
                  className="rounded-lg bg-[#091c2d] px-5 py-2 text-sm font-bold text-white shadow-sm transition hover:bg-[#1f3143] disabled:opacity-50"
                >
                  {t('accountProfile.changePhoto')}
                </button>
              </div>
            </div>
          </section>

          {/* Account details — header strip + body */}
          <Formik
            enableReinitialize
            validationSchema={profileSchema}
            initialValues={{ fullName: user?.fullName || '' }}
            onSubmit={async (values, { setSubmitting }) => {
              setSubmitting(true)
              const res = await apiPut('user/me/profile', { fullName: values.fullName })
              setSubmitting(false)
              if (res?.success) {
                notify.success(t('accountProfile.saved'))
                await mergeSessionUser()
              }
            }}
          >
            {({ isSubmitting, errors, touched, resetForm }) => (
              <section className={classNames(cardShell, 'overflow-hidden', isRTL && 'text-right')}>
                <div className="border-b border-[#f2f4f6] p-8">
                  <h2 className="text-lg font-bold text-[#091c2d]">{t('accountProfile.sectionAccount')}</h2>
                  <p className="mt-1 text-sm text-[#454652]">{t('accountProfile.accountDetailsLead')}</p>
                </div>
                <div className="space-y-6 p-8">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-[#454652]">{t('accountProfile.email')}</label>
                    <div
                      className={classNames('flex cursor-not-allowed items-center gap-3 rounded-lg border border-[#c6c5d4]/20 bg-[#f2f4f6] px-4 py-3', isRTL && 'flex-row-reverse')}
                      dir="ltr"
                    >
                      <MailIcon className="h-5 w-5 shrink-0 text-[#454652]" strokeWidth={1.75} aria-hidden />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#454652]">{user?.email || '—'}</span>
                      <LockClosedIcon className="h-4 w-4 shrink-0 text-[#767683]" aria-hidden title={t('accountProfile.emailHint')} />
                    </div>
                  </div>

                  <Form className="space-y-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold uppercase tracking-wider text-[#454652]" htmlFor="profile-fullName">
                        {t('accountProfile.fullName')}
                      </label>
                      <div className="relative group">
                        <UserIcon
                          className="pointer-events-none absolute left-4 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-[#767683] transition-colors group-focus-within:text-[#091c2d] rtl:left-auto rtl:right-4"
                          strokeWidth={1.75}
                          aria-hidden
                        />
                        <Field
                          id="profile-fullName"
                          name="fullName"
                          type="text"
                          autoComplete="name"
                          className="w-full rounded-lg border border-[#c6c5d4]/40 bg-white py-3 pl-12 pr-4 text-sm font-medium text-[#191c1e] outline-none transition-all placeholder:text-[#767683] focus:border-[#091c2d] focus:ring-2 focus:ring-[#091c2d]/10 rtl:pl-4 rtl:pr-12"
                        />
                      </div>
                      {touched.fullName && errors.fullName ? <p className="text-sm font-medium text-[#ba1a1a]">{errors.fullName}</p> : null}
                    </div>

                    <div className={classNames('flex items-center gap-4 border-t border-transparent pt-4', isRTL ? 'justify-start' : 'justify-end')}>
                      <button type="button" onClick={() => resetForm()} className="px-6 text-sm font-bold text-[#454652] transition hover:text-[#091c2d]">
                        {t('accountProfile.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex min-w-[160px] items-center justify-center gap-2 rounded-lg bg-gradient-to-br from-[#091c2d] to-[#1f3143] px-8 py-3 text-sm font-bold text-white shadow-lg shadow-[#091c2d]/10 transition hover:opacity-90 disabled:opacity-60"
                      >
                        {isSubmitting ? <LoadingIcon className="text-white" /> : null}
                        {isSubmitting ? t('accountProfile.saving') : t('accountProfile.save')}
                      </button>
                    </div>
                  </Form>
                </div>
              </section>
            )}
          </Formik>

          {/* Security — single row */}
          <section className={classNames(cardShell, 'flex flex-col gap-6 p-8 sm:flex-row sm:items-center sm:justify-between', isRTL && 'sm:flex-row-reverse')}>
            <div className={classNames('flex gap-4', isRTL && 'flex-row-reverse text-right')}>
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#ffdad6]/30 text-[#ba1a1a]">
                <RefreshIcon className="h-6 w-6" strokeWidth={1.75} aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-[#091c2d]">{t('accountProfile.passwordTitle')}</h2>
                <p className="text-sm text-[#454652]">{t('accountProfile.passwordSubtitle')}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => openChangePasswordModal()}
              className={classNames(
                'inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-[#e0e3e5] px-6 py-3 text-sm font-bold text-[#191c1e] transition hover:bg-[#e6e8ea]',
                isRTL && 'flex-row-reverse'
              )}
            >
              <PencilIcon className="h-4 w-4 text-[#454652]" strokeWidth={2} aria-hidden />
              {t('accountProfile.changePassword')}
            </button>
          </section>

          {/* Admin callout */}
          <div className={classNames('flex gap-3 rounded-xl border border-[#cde2f8]/40 bg-[#cde2f8]/20 p-4', isRTL && 'flex-row-reverse text-right')}>
            <InformationCircleIcon className="h-6 w-6 shrink-0 text-[#4c6073]" strokeWidth={1.75} aria-hidden />
            <p className="text-xs font-medium leading-relaxed text-[#516578]">{t('accountProfile.adminCallout')}</p>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default MyProfile
